import { ApiConfiguration, ApiConfigurationOptions } from './ApiConfiguration';
import { MainClient } from './clients/MainClient';
import { SalesforceClient } from './clients/SalesforceClient';
import { GenericClient } from './clients/GenericClient';
import { CoreClient } from './clients/CoreClient';
import { FilesClient } from './clients/FilesClient';
import { GoogleClient } from './clients/GoogleClient';
import { ProductCatalogClient } from './clients/ProductCatalogClient';
import { UserClient } from './clients/UserClient';
import { CatalogFeedClient } from './clients/CatalogFeedClient';
import { EntityClient } from './clients/EntityClient';
import { EntityOwnedModelClient } from './clients/EntityOwnedModelClient';
import { LeadTypeClient } from './clients/LeadTypeClient';
import { ObjectTypeClient } from './clients/ObjectTypeClient';
import { OrganizationClient } from './clients/OrganizationClient';
import { AiClient } from './clients/AiClient';
import { AppOtgClient } from './clients/AppOtgClient';
import { DocusealClient } from './clients/DocusealClient';
import { Fcb2bClient } from './clients/Fcb2bClient';
import { O365Client } from './clients/O365Client';
import { OtgClient } from './clients/OtgClient';
import { ReportClient } from './clients/ReportClient';
import { WebpunchClient } from './clients/WebpunchClient';

/**
 * Unified API client providing access to all API endpoints
 *
 * All clients share the same configuration instance, ensuring consistent
 * authentication, headers, interceptors, and timeout settings.
 *
 * ProgramInterface.com API client
 */
export class Api {
  private readonly _config: ApiConfiguration;
  private _main?: MainClient;
  private _salesforce?: SalesforceClient;
  private _generic?: GenericClient;
  private _core?: CoreClient;
  private _files?: FilesClient;
  private _google?: GoogleClient;
  private _productCatalog?: ProductCatalogClient;
  private _user?: UserClient;
  private _catalogFeed?: CatalogFeedClient;
  private _entity?: EntityClient;
  private _entityOwnedModel?: EntityOwnedModelClient;
  private _leadType?: LeadTypeClient;
  private _objectType?: ObjectTypeClient;
  private _organization?: OrganizationClient;
  private _ai?: AiClient;
  private _appOtg?: AppOtgClient;
  private _docuseal?: DocusealClient;
  private _fcb2b?: Fcb2bClient;
  private _o365?: O365Client;
  private _otg?: OtgClient;
  private _report?: ReportClient;
  private _webpunch?: WebpunchClient;

  constructor(options?: ApiConfigurationOptions) {
    this._config = new ApiConfiguration(options);
  }

  /**
   * Get the shared API configuration
   *
   * Use this to configure authentication, headers, interceptors, and timeouts
   * that will be applied to all API clients.
   */
  get config(): ApiConfiguration {
    return this._config;
  }

  /**
   * Get the Main API client
   *
   * Client for Main operations
   */
  get main(): MainClient {
    if (!this._main) {
      this._main = new MainClient(this._config);
    }
    return this._main;
  }

  /**
   * Get the Salesforce API client
   *
   * Client for Salesforce operations
   */
  get salesforce(): SalesforceClient {
    if (!this._salesforce) {
      this._salesforce = new SalesforceClient(this._config);
    }
    return this._salesforce;
  }

  /**
   * Get the Generic API client
   *
   * Client for Generic operations
   */
  get generic(): GenericClient {
    if (!this._generic) {
      this._generic = new GenericClient(this._config);
    }
    return this._generic;
  }

  /**
   * Get the Core API client
   *
   * Client for Core operations
   */
  get core(): CoreClient {
    if (!this._core) {
      this._core = new CoreClient(this._config);
    }
    return this._core;
  }

  /**
   * Get the Files API client
   *
   * Client for Files operations
   */
  get files(): FilesClient {
    if (!this._files) {
      this._files = new FilesClient(this._config);
    }
    return this._files;
  }

  /**
   * Get the Google API client
   *
   * Client for Google operations
   */
  get google(): GoogleClient {
    if (!this._google) {
      this._google = new GoogleClient(this._config);
    }
    return this._google;
  }

  /**
   * Get the ProductCatalog API client
   *
   * Client for ProductCatalog operations
   */
  get productCatalog(): ProductCatalogClient {
    if (!this._productCatalog) {
      this._productCatalog = new ProductCatalogClient(this._config);
    }
    return this._productCatalog;
  }

  /**
   * Get the User API client
   *
   * Client for User operations
   */
  get user(): UserClient {
    if (!this._user) {
      this._user = new UserClient(this._config);
    }
    return this._user;
  }

  /**
   * Get the CatalogFeed API client
   *
   * Client for CatalogFeed operations
   */
  get catalogFeed(): CatalogFeedClient {
    if (!this._catalogFeed) {
      this._catalogFeed = new CatalogFeedClient(this._config);
    }
    return this._catalogFeed;
  }

  /**
   * Get the Entity API client
   *
   * Client for Entity operations
   */
  get entity(): EntityClient {
    if (!this._entity) {
      this._entity = new EntityClient(this._config);
    }
    return this._entity;
  }

  /**
   * Get the EntityOwnedModel API client
   *
   * Client for EntityOwnedModel operations
   */
  get entityOwnedModel(): EntityOwnedModelClient {
    if (!this._entityOwnedModel) {
      this._entityOwnedModel = new EntityOwnedModelClient(this._config);
    }
    return this._entityOwnedModel;
  }

  /**
   * Get the LeadType API client
   *
   * Client for LeadType operations
   */
  get leadType(): LeadTypeClient {
    if (!this._leadType) {
      this._leadType = new LeadTypeClient(this._config);
    }
    return this._leadType;
  }

  /**
   * Get the ObjectType API client
   *
   * Client for ObjectType operations
   */
  get objectType(): ObjectTypeClient {
    if (!this._objectType) {
      this._objectType = new ObjectTypeClient(this._config);
    }
    return this._objectType;
  }

  /**
   * Get the Organization API client
   *
   * Client for Organization operations
   */
  get organization(): OrganizationClient {
    if (!this._organization) {
      this._organization = new OrganizationClient(this._config);
    }
    return this._organization;
  }

  /**
   * Get the Ai API client
   *
   * Client for Ai operations
   */
  get ai(): AiClient {
    if (!this._ai) {
      this._ai = new AiClient(this._config);
    }
    return this._ai;
  }

  /**
   * Get the AppOtg API client
   *
   * Client for AppOtg operations
   */
  get appOtg(): AppOtgClient {
    if (!this._appOtg) {
      this._appOtg = new AppOtgClient(this._config);
    }
    return this._appOtg;
  }

  /**
   * Get the Docuseal API client
   *
   * Client for Docuseal operations
   */
  get docuseal(): DocusealClient {
    if (!this._docuseal) {
      this._docuseal = new DocusealClient(this._config);
    }
    return this._docuseal;
  }

  /**
   * Get the Fcb2B API client
   *
   * Client for Fcb2B operations
   */
  get fcb2b(): Fcb2bClient {
    if (!this._fcb2b) {
      this._fcb2b = new Fcb2bClient(this._config);
    }
    return this._fcb2b;
  }

  /**
   * Get the O365 API client
   *
   * Client for O365 operations
   */
  get o365(): O365Client {
    if (!this._o365) {
      this._o365 = new O365Client(this._config);
    }
    return this._o365;
  }

  /**
   * Get the Otg API client
   *
   * Client for Otg operations
   */
  get otg(): OtgClient {
    if (!this._otg) {
      this._otg = new OtgClient(this._config);
    }
    return this._otg;
  }

  /**
   * Get the Report API client
   *
   * Client for Report operations
   */
  get report(): ReportClient {
    if (!this._report) {
      this._report = new ReportClient(this._config);
    }
    return this._report;
  }

  /**
   * Get the Webpunch API client
   *
   * Client for Webpunch operations
   */
  get webpunch(): WebpunchClient {
    if (!this._webpunch) {
      this._webpunch = new WebpunchClient(this._config);
    }
    return this._webpunch;
  }

}
